<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Compliance documental</h1>
    </div>

    <!-- Cards resumen -->
    <div class="row mb-3">
      <div class="col-6 col-lg-3 mb-2" v-for="c in cards" :key="c.key">
        <div class="summary-card" :class="c.key">
          <span class="summary-card__label">{{ c.label }}</span>
          <span class="summary-card__value">{{ c.value }}</span>
        </div>
      </div>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-md-none">
                <button
                  type="button"
                  class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover
                  title="Recargar"
                  @click="loadData()"
                >
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button
                type="button"
                class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover
                title="Recargar"
                @click="loadData()"
              >
                <i class="ri-loop-right-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por nombre o placa"
                @search="onSearch"
                @input="search = $event"
                class="flex-1"
              />
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="showFiltersList">
          <span v-for="(val, name) in filterList" v-bind:key="name" class="filter-item">
            {{ getFilterName(name, val) }}
            <button
              type="button"
              class="text-white p-0 bg-transparent border-0 ml-2"
              @click="deleteFilter(name)"
            >
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-4">Entidad</th>
              <th class="text-center">Tipo</th>
              <th>Proveedor</th>
              <th class="text-center">Requeridos</th>
              <th class="text-center">Al día</th>
              <th class="text-center">Por vencer</th>
              <th class="text-center">Vencidos</th>
              <th class="text-center">Faltantes</th>
              <th class="text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.entity_type + '-' + row.entity_id" class="clickable-row" @click="goTo(row)">
              <td class="pl-4 font-weight-bold">{{ row.entity_name }}</td>
              <td class="text-center">
                <span :class="`badge ${entityMeta(row.entity_type).color}`">
                  {{ entityMeta(row.entity_type).label }}
                </span>
              </td>
              <td>{{ row.provider_name || "TravelCab" }}</td>
              <td class="text-center">{{ row.required_docs }}</td>
              <td class="text-center">{{ row.valid_docs }}</td>
              <td class="text-center" :class="{ 'text-orange': row.expiring_docs > 0 }">{{ row.expiring_docs }}</td>
              <td class="text-center" :class="{ 'text-red': row.expired_docs > 0 }">{{ row.expired_docs }}</td>
              <td class="text-center" :class="{ 'text-red': row.missing_docs > 0 }">{{ row.missing_docs }}</td>
              <td class="text-center">
                <span :class="`badge ${statusMeta(row.status).color}`">{{ statusMeta(row.status).label }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center" v-if="!rows.length">
        <div class="table-no-results">
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Alert,
  Constants,
  Util,
  SearchBar,
  FilterMenu,
  FilterMixin,
  PaginationMixin,
} from "wize-admin";

import { DocumentService, ProviderService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

// Dashboard de compliance documental (DEF-ADM-019 §4.4). Cubre vehículos,
// conductores y proveedores de la ciudad activa, propios y de terceros.
export default {
  name: "CompliancePage",
  components: { SearchBar, FilterMenu },
  mixins: [PaginationMixin, FilterMixin],
  data() {
    return {
      rows: [],
      providers: [],
      summary: { valid: 0, expiring: 0, expired: 0, incomplete: 0 },
      filterOptions: [
        {
          label: "Tipo de entidad",
          key: "entity_type",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.DocumentEntity.VEHICLE,
            LocalConstants.DocumentEntity.DRIVER,
            LocalConstants.DocumentEntity.PROVIDER,
          ],
        },
        {
          label: "Proveedor",
          key: "provider_id",
          type: Constants.TypeFilters.SINGLE,
          options: [],
        },
        {
          label: "Estado",
          key: "status",
          type: Constants.TypeFilters.SINGLE,
          options: Object.values(LocalConstants.ComplianceStatus),
        },
      ],
    };
  },
  computed: {
    // Mismas cards que el resto del admin (Gastos de flota): etiqueta arriba,
    // valor grande y franja de color a la izquierda.
    cards() {
      return [
        { key: "valid", label: "Al día", value: this.summary.valid },
        { key: "expiring", label: "Por vencer (30 días)", value: this.summary.expiring },
        { key: "expired", label: "Vencidos", value: this.summary.expired },
        { key: "incomplete", label: "Incompletos", value: this.summary.incomplete },
      ];
    },
  },
  methods: {
    entityMeta(key) {
      return Util.searchValue(key, LocalConstants.DocumentEntity) || { label: key, color: "badge-gray" };
    },
    statusMeta(key) {
      return Util.searchValue(key, LocalConstants.ComplianceStatus) || { label: key, color: "badge-gray" };
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const params = { ...this.filters };
        if (this.search) params.search = this.search;
        const res = await DocumentService.compliance(params);
        this.rows = res.payload.rows || [];
        this.summary = res.payload.summary || { valid: 0, expiring: 0, expired: 0, incomplete: 0 };
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo cargar el compliance");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async loadProviders() {
      try {
        const res = await ProviderService.list({ status: 1 }, { page: 0, size: 200 });
        this.providers = res.payload.items || [];
        const option = this.filterOptions.find((o) => o.key === "provider_id");
        option.options = this.providers.map((p) => ({
          key: p.id,
          label: p.business_name || p.contact_name,
        }));
      } catch (ex) {
        console.error(ex);
        this.providers = [];
      }
    },
    // Lleva al detalle de la entidad, donde está su tab Documentos.
    goTo(row) {
      if (row.entity_type === "PROVIDER") {
        this.$router.push({ name: "providers-detail", params: { id: row.entity_id } });
      } else if (row.entity_type === "VEHICLE") {
        this.$router.push({ name: "config-vehicles-edit", params: { id: row.entity_id } });
      } else {
        this.$router.push({ name: "config-drivers-edit", params: { id: row.entity_id } });
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadProviders()]);
  },
};
</script>

<style lang="stylus" scoped>
// Cards con el mismo formato que las de Gastos de flota.
.summary-card
  background #fff
  border 1px solid #e5e7eb
  border-radius 10px
  padding 14px 16px
  display flex
  flex-direction column
  height 100%
  &.valid
    border-left 4px solid #28a745
  &.expiring
    border-left 4px solid #fd7e14
  &.expired
    border-left 4px solid #dc3545
  &.incomplete
    border-left 4px solid #8592A6
.summary-card__label
  font-size 11px
  text-transform uppercase
  letter-spacing .05em
  color #8592A6
.summary-card__value
  font-size 20px
  font-weight 700
  margin-top 4px
.text-orange
  color #fd7e14
.text-red
  color #dc3545
.clickable-row
  cursor pointer
</style>
